<template>
  <!-- Chat button that toggles the visibility of the chat overlay -->
  <div id="tcw-chat-button" @click="toggle" v-show="!isOpen">
    <div class="tcw-button-inner">
      <svg class="tcw-chat-icon" viewBox="0 0 24 24">
        <path
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        ></path>
      </svg>
    </div>
    <div
      id="tcw-unread-badge"
      :class="{ 'tcw-hidden': isOpen || unreadCount === 0 }"
    >
      <span id="tcw-unread-count">{{ unreadCount }}</span>
    </div>
  </div>

  <!-- Chat overlay shown when the widget is open -->
  <div id="tcw-chat-overlay" :class="{ 'tcw-hidden': !isOpen }">
    <!-- Connection status banner -->
    <div
      id="tcw-connection-status"
      :class="{ 'tcw-hidden': connectionStatus === 'online' }"
    >
      <span class="tcw-connection-text">No connection. Retrying...</span>
    </div>

    <!-- Header with status dot, title and actions -->
    <div class="tcw-chat-header">
      <div class="tcw-header-left">
        <div
          class="tcw-status-dot"
          :class="{ 'tcw-offline': connectionStatus !== 'online' }"
        ></div>
        <h3 class="tcw-header-title">Team Chat</h3>
      </div>
      <div class="tcw-header-actions">
        <button
          id="tcw-sound-toggle"
          :class="{ 'tcw-muted': !audioEnabled }"
          title="Toggle sound"
          @click="toggleSound"
        >
          <svg viewBox="0 0 24 24">
            <path
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            ></path>
          </svg>
        </button>
        <button id="tcw-close-chat" title="Close" @click="toggle">
          <svg viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Online users bar -->
    <div class="tcw-online-bar">
      <!-- Online content wrapper - slides right to hide -->
      <div class="tcw-online-wrapper" :class="{ 'tcw-slide-out': typingUsers.size > 0 }">
        <span class="tcw-online-label">Online:</span>
        <div id="tcw-online-list">
          <span
            v-for="user in filteredOnlineUsers"
            :key="user.userId"
            class="tcw-online-user"
            :data-username="user.userName"
            @click="insertMentionFromOnlineUser(user.userName)"
            >{{ user.userName }}</span
          >
          <span v-if="!filteredOnlineUsers.length" class="tcw-no-users"
            >No one else online</span
          >
        </div>
      </div>

      <!-- Typing container - slides in from left to show -->
      <div v-if="typingUsers.size > 0" class="tcw-typing-wrapper">
        {{ getTypingText() }}
      </div>
    </div>

    <!-- Messages container -->
    <div id="tcw-messages" ref="messagesContainer" @scroll="handleScroll">
      <!-- Loading indicator for older messages -->
      <div v-if="loadingOldMessages" class="tcw-loading" style="opacity: 1">
        <div class="tcw-spinner"></div>
        Loading...
      </div>

      <!-- No more messages indicator -->
      <div v-if="!hasMoreMessages && messages.size > 0" class="tcw-no-more">
        No more messages
      </div>

      <!-- Welcome message when no messages -->
      <div v-if="messages.size === 0" id="tcw-welcome">
        <svg
          viewBox="0 0 200 200"
          style="width: 80px; height: 80px; margin-bottom: 16px"
        >
          <defs>
            <linearGradient
              id="chatGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" style="stop-color: #667eea; stop-opacity: 1" />
              <stop offset="50%" style="stop-color: #764ba2; stop-opacity: 1" />
              <stop
                offset="100%"
                style="stop-color: #f093fb; stop-opacity: 1"
              />
            </linearGradient>
          </defs>
          <!-- Main chat bubble -->
          <rect
            x="40"
            y="50"
            width="120"
            height="80"
            rx="20"
            fill="url(#chatGradient)"
            opacity="0.9"
          />
          <!-- Chat bubble tail -->
          <path
            d="M60 130 L40 150 L70 130"
            fill="url(#chatGradient)"
            opacity="0.9"
          />
          <!-- Message dots -->
          <circle cx="70" cy="90" r="6" fill="white" opacity="0.9">
            <animate
              attributeName="opacity"
              values="0.9;0.3;0.9"
              dur="1.5s"
              repeatCount="indefinite"
              begin="0s"
            />
          </circle>
          <circle cx="100" cy="90" r="6" fill="white" opacity="0.9">
            <animate
              attributeName="opacity"
              values="0.9;0.3;0.9"
              dur="1.5s"
              repeatCount="indefinite"
              begin="0.3s"
            />
          </circle>
          <circle cx="130" cy="90" r="6" fill="white" opacity="0.9">
            <animate
              attributeName="opacity"
              values="0.9;0.3;0.9"
              dur="1.5s"
              repeatCount="indefinite"
              begin="0.6s"
            />
          </circle>
        </svg>
        <p style="color: #6c757d; font-size: 16px; margin: 0">
          Start a conversation!
        </p>
      </div>

      <!-- Message items -->
      <div
        v-for="msg in sortedMessages"
        :key="msg.id"
        class="tcw-message-item"
        :class="{
          'tcw-own': msg.userId === config.userId,
          'tcw-mentioned': isMentioned(msg),
        }"
        :data-message-id="msg.id"
        :data-seen-by-me="msg.seenByMe ? 'true' : undefined"
      >
        <div class="tcw-message-wrapper">
          <div class="tcw-message-bubble">
            <!-- Reply preview if replying to another message -->
            <div v-if="msg.replyTo" class="tcw-reply-preview">
              <div class="tcw-reply-author">{{ msg.replyTo.userName }}</div>
              <div class="tcw-reply-text">
                {{ truncateReply(msg.replyTo.content) }}
              </div>
            </div>

            <!-- User name (not shown for own messages) -->
            <div v-if="msg.userId !== config.userId" class="tcw-message-name">
              {{ msg.userName }}
            </div>

            <!-- Message content -->

            <img
              v-if="msg.type === 'image'"
              :src="msg.content"
              class="tcw-image-thumb"
              @click="openImageViewer(msg)"
              alt="Shared image"
            />
            <div v-else>
              <div
                class="tcw-message-content"
                :class="{ 'tcw-single-emoji-shake': isSingleEmojiMessage(msg.content) && newlySentMessageIds.has(msg.id) }"
                v-html="processMentions(msg.content)"
              ></div>

              <!-- Link previews -->
              <div
                v-if="getMessageUrls(msg.content).length > 0"
                class="tcw-link-previews"
              >
                <a
                  v-for="url in getMessageUrls(msg.content)"
                  :key="url"
                  v-show="messagePreviews[url]"
                  :href="url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="tcw-link-preview"
                >
                  <img
                    v-if="messagePreviews[url]?.image"
                    :src="messagePreviews[url].image"
                    :alt="messagePreviews[url].title"
                    class="tcw-link-preview-image"
                  />
                  <div class="tcw-link-preview-content">
                    <h4 class="tcw-link-preview-title">
                      {{ messagePreviews[url]?.title }}
                    </h4>
                    <p class="tcw-link-preview-description">
                      {{ messagePreviews[url]?.description }}
                    </p>
                    <span class="tcw-link-preview-url">{{
                      messagePreviews[url]?.url
                    }}</span>
                  </div>
                </a>
              </div>
            </div>

            <!-- Message metadata -->
            <div class="tcw-message-meta">
              <span class="tcw-message-time">{{
                formatTime(msg.timestamp)
              }}</span>
              <span
                v-if="msg.status === 'sending'"
                class="tcw-message-status tcw-sending"
              ></span>
              <button
                v-if="msg.status === 'failed'"
                class="tcw-retry-btn"
                @click="retryMessage(msg)"
                title="Retry"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M4 12a8 8 0 0 1 8-8V2.5M20 12a8 8 0 0 1-8 8v1.5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    fill="none"
                  />
                  <path
                    d="M12 4L8 8l4 4"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  />
                </svg>
              </button>

              <!-- Seen indicator for own messages -->
              <span
                v-if="msg.userId === config.userId && getSeenCount(msg) > 0"
                class="tcw-seen-indicator"
                :data-seen-users="getSeenUserNames(msg)"
                @click="showSeenModal(msg)"
              >
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    <path d="M2.305,11.235a1,1,0,0,1,1.414.024l3.206,3.319L14.3,7.289A1,1,0,0,1,15.7,8.711l-8.091,8a1,1,0,0,1-.7.289H6.9a1,1,0,0,1-.708-.3L2.281,12.649A1,1,0,0,1,2.305,11.235ZM20.3,7.289l-7.372,7.289-.263-.273a1,1,0,1,0-1.438,1.39l.966,1a1,1,0,0,0,.708.3h.011a1,1,0,0,0,.7-.289l8.091-8A1,1,0,0,0,20.3,7.289Z" fill="#03b300"></path>
                  </g>
                </svg>
                {{ getSeenCount(msg) }}
              </span>
            </div>
          </div>

          <!-- Reactions display -->
          <div v-if="hasReactions(msg)" class="tcw-reactions-display">
            <span
              v-if="msg.reactions?.thumbsUp?.length"
              class="tcw-reaction-badge"
              :class="{ 'tcw-my-reaction': hasMyReaction(msg, 'thumbsUp') }"
              :data-reaction="'thumbsUp'"
              :data-users="getReactionUserNames(msg, 'thumbsUp')"
              @click="showReactionModal(msg, 'thumbsUp')"
            >
              <svg height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xml:space="preserve">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <g>
                    <path style="fill:#E9A440;" d="M473.534,302.894c0-0.599-0.02-1.195-0.062-1.783l-0.02-31.697 c0-29.045-11.313-56.356-31.851-76.894c-20.54-20.54-47.85-31.851-76.895-31.851h-90.957l26.014-51.688 c5.2-10.378,7.95-22.029,7.95-33.695C307.712,33.773,273.941,0,232.426,0c-28.704,0-54.515,15.952-67.363,41.637L57.569,256.6 c-12.497,24.496-19.103,52.064-19.103,79.735C38.466,433.196,117.27,512,214.133,512h150.572 c59.963,0,108.746-48.782,108.746-108.725l0.007-8.109c0.05-0.651,0.075-1.305,0.075-1.967l-0.033-49.854L473.534,302.894z"></path>
                    <path style="fill:#E9A440;" d="M473.534,302.894c0-0.599-0.02-1.195-0.062-1.783l-0.02-31.697 c0-29.045-11.313-56.356-31.851-76.894c-20.54-20.54-47.85-31.851-76.895-31.851h-90.957l26.014-51.688 c5.2-10.378,7.95-22.029,7.95-33.695C307.712,33.773,273.941,0,232.426,0c-28.704,0-54.515,15.952-67.363,41.637L57.569,256.6 c-12.497,24.496-19.103,52.064-19.103,79.735C38.466,433.196,117.27,512,214.133,512h150.572 c59.963,0,108.746-48.782,108.746-108.725l0.007-8.109c0.05-0.651,0.075-1.305,0.075-1.967l-0.033-49.854L473.534,302.894z M423.342,391.124c-0.047,0.624-0.072,1.255-0.072,1.889l-0.008,10.241c0,32.289-26.268,58.556-58.556,58.556H255.999h-41.866 c-69.188,0-125.477-56.289-125.477-125.475c0-20.086,4.594-39.261,13.655-56.993c0.032-0.064,0.065-0.129,0.099-0.194 l107.542-215.06c4.288-8.571,12.899-13.896,22.474-13.896c0.786,0,1.561,0.047,2.329,0.117c0.244,0.023,0.484,0.057,0.726,0.087 c0.527,0.065,1.051,0.144,1.568,0.239c0.268,0.05,0.534,0.104,0.798,0.161c0.509,0.114,1.011,0.246,1.506,0.388 c0.234,0.067,0.47,0.125,0.701,0.199c0.674,0.217,1.338,0.458,1.989,0.728c0.266,0.11,0.52,0.243,0.783,0.363 c0.391,0.179,0.783,0.361,1.163,0.56c0.328,0.171,0.647,0.353,0.965,0.539c0.289,0.167,0.574,0.34,0.857,0.52 c0.346,0.219,0.689,0.442,1.024,0.678c0.197,0.139,0.385,0.288,0.579,0.432c1.312,0.982,2.521,2.085,3.617,3.299 c0.067,0.074,0.134,0.146,0.199,0.219c0.381,0.433,0.746,0.878,1.098,1.337c0.032,0.042,0.064,0.085,0.095,0.125 c1.502,1.986,2.714,4.196,3.579,6.58c0,0,0.002,0.002,0.002,0.003c0,0,0,0.002,0,0.003c0.233,0.636,0.438,1.283,0.617,1.942 c0.025,0.087,0.044,0.174,0.067,0.261c0.154,0.589,0.296,1.179,0.405,1.783c0.05,0.268,0.084,0.542,0.125,0.813 c0.067,0.455,0.144,0.907,0.186,1.369c0.075,0.773,0.119,1.554,0.119,2.346c0,0.885-0.059,1.758-0.149,2.623 c-0.03,0.288-0.072,0.569-0.11,0.853c-0.082,0.584-0.179,1.159-0.301,1.732c-0.065,0.316-0.132,0.632-0.211,0.944 c-0.154,0.611-0.336,1.213-0.537,1.809c-0.074,0.223-0.13,0.448-0.212,0.669c0,0,0,0,0,0.002c-0.318,0.862-0.676,1.708-1.089,2.535 l-44.302,88.028c-3.917,7.78-3.518,17.033,1.051,24.448c4.569,7.415,12.656,11.929,21.366,11.929h22.976l108.706-0.002 c32.288,0,58.556,26.268,58.556,58.573l0.022,33.591c0,0.574,0.02,1.143,0.059,1.708l-0.03,38.615L423.342,391.124z"></path>
                  </g>
                  <path style="fill:#EFC27B;" d="M256.001,461.809h108.706c32.288,0,58.556-26.266,58.556-58.556l0.008-10.241 c0-0.634,0.025-1.265,0.072-1.889l-0.03-47.783l0.03-38.615c-0.038-0.565-0.059-1.134-0.059-1.707l-0.022-33.591 c0-32.304-26.268-58.572-58.556-58.572l-108.706,0.002V461.809z"></path>
                  <g>
                    <path style="fill:#ECB45C;" d="M214.133,461.809h41.866V210.858h-22.976c-8.708,0-16.795-4.514-21.365-11.929 c-4.567-7.417-4.967-16.668-1.051-24.448l44.302-88.028c0.413-0.825,0.773-1.673,1.089-2.535V66.765 c0-0.002-0.002-0.003-0.002-0.003c-0.865-2.381-2.078-4.594-3.579-6.58c-0.032-0.042-0.062-0.084-0.095-0.125 c-0.35-0.458-0.718-0.903-1.098-1.337c-0.065-0.074-0.132-0.146-0.199-0.219c-1.092-1.215-2.309-2.315-3.617-3.299 c-0.192-0.144-0.381-0.294-0.579-0.432c-0.335-0.238-0.679-0.458-1.024-0.678c-0.283-0.177-0.567-0.353-0.857-0.519 c-0.318-0.186-0.639-0.368-0.965-0.539c-0.38-0.199-0.771-0.383-1.163-0.56c-0.261-0.12-0.517-0.249-0.783-0.363 c-0.651-0.269-1.313-0.512-1.989-0.728c-0.231-0.074-0.468-0.132-0.701-0.199c-0.497-0.142-0.999-0.276-1.506-0.388 c-0.264-0.057-0.53-0.109-0.798-0.161c-0.517-0.097-1.039-0.174-1.568-0.241c-0.243-0.03-0.482-0.064-0.726-0.087 c-0.768-0.07-1.543-0.117-2.329-0.117c-9.575,0-18.184,5.325-22.474,13.896l-107.542,215.06c-0.033,0.065-0.065,0.13-0.099,0.194 c-9.061,17.734-13.655,36.909-13.655,56.993C88.657,405.522,144.946,461.809,214.133,461.809z"></path>
                  </g>
                </g>
              </svg>
              {{ msg.reactions.thumbsUp.length }}
            </span>
            <span
              v-if="msg.reactions?.heart?.length"
              class="tcw-reaction-badge"
              :class="{ 'tcw-my-reaction': hasMyReaction(msg, 'heart') }"
              :data-reaction="'heart'"
              :data-users="getReactionUserNames(msg, 'heart')"
              @click="showReactionModal(msg, 'heart')"
            >
              <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M1.24264 8.24264L8 15L14.7574 8.24264C15.553 7.44699 16 6.36786 16 5.24264V5.05234C16 2.8143 14.1857 1 11.9477 1C10.7166 1 9.55233 1.55959 8.78331 2.52086L8 3.5L7.21669 2.52086C6.44767 1.55959 5.28338 1 4.05234 1C1.8143 1 0 2.8143 0 5.05234V5.24264C0 6.36786 0.44699 7.44699 1.24264 8.24264Z" fill="#ff0000"></path>
                </g>
              </svg>
              {{ msg.reactions.heart.length }}
            </span>
          </div>

          <!-- Message actions -->
          <div class="tcw-message-actions">
            <button
              class="tcw-reply-btn"
              @click="setReplyTo(msg)"
              title="Reply"
            >
              <svg viewBox="0 0 8 8" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M3.5 0c-1.93 0-3.5 1.57-3.5 3.5 0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v.5h-1l2 2 2-2h-1v-.5c0-1.93-1.57-3.5-3.5-3.5z" transform="translate(0 1)"></path>
                </g>
              </svg>
            </button>
            <div
              v-if="msg.userId !== config.userId"
              class="tcw-reaction-buttons"
            >
              <button
                class="tcw-react-btn"
                :class="{
                  'tcw-reaction-shake': shakeReactions[`${msg.id}-thumbsUp`],
                }"
                data-reaction="thumbsUp"
                @click="() => toggleReactionWithShake(msg.id, 'thumbsUp')"
              >
                <svg height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xml:space="preserve">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    <g>
                      <path style="fill:#E9A440;" d="M473.534,302.894c0-0.599-0.02-1.195-0.062-1.783l-0.02-31.697 c0-29.045-11.313-56.356-31.851-76.894c-20.54-20.54-47.85-31.851-76.895-31.851h-90.957l26.014-51.688 c5.2-10.378,7.95-22.029,7.95-33.695C307.712,33.773,273.941,0,232.426,0c-28.704,0-54.515,15.952-67.363,41.637L57.569,256.6 c-12.497,24.496-19.103,52.064-19.103,79.735C38.466,433.196,117.27,512,214.133,512h150.572 c59.963,0,108.746-48.782,108.746-108.725l0.007-8.109c0.05-0.651,0.075-1.305,0.075-1.967l-0.033-49.854L473.534,302.894z"></path>
                      <path style="fill:#E9A440;" d="M473.534,302.894c0-0.599-0.02-1.195-0.062-1.783l-0.02-31.697 c0-29.045-11.313-56.356-31.851-76.894c-20.54-20.54-47.85-31.851-76.895-31.851h-90.957l26.014-51.688 c5.2-10.378,7.95-22.029,7.95-33.695C307.712,33.773,273.941,0,232.426,0c-28.704,0-54.515,15.952-67.363,41.637L57.569,256.6 c-12.497,24.496-19.103,52.064-19.103,79.735C38.466,433.196,117.27,512,214.133,512h150.572 c59.963,0,108.746-48.782,108.746-108.725l0.007-8.109c0.05-0.651,0.075-1.305,0.075-1.967l-0.033-49.854L473.534,302.894z M423.342,391.124c-0.047,0.624-0.072,1.255-0.072,1.889l-0.008,10.241c0,32.289-26.268,58.556-58.556,58.556H255.999h-41.866 c-69.188,0-125.477-56.289-125.477-125.475c0-20.086,4.594-39.261,13.655-56.993c0.032-0.064,0.065-0.129,0.099-0.194 l107.542-215.06c4.288-8.571,12.899-13.896,22.474-13.896c0.786,0,1.561,0.047,2.329,0.117c0.244,0.023,0.484,0.057,0.726,0.087 c0.527,0.065,1.051,0.144,1.568,0.239c0.268,0.05,0.534,0.104,0.798,0.161c0.509,0.114,1.011,0.246,1.506,0.388 c0.234,0.067,0.47,0.125,0.701,0.199c0.674,0.217,1.338,0.458,1.989,0.728c0.266,0.11,0.52,0.243,0.783,0.363 c0.391,0.179,0.783,0.361,1.163,0.56c0.328,0.171,0.647,0.353,0.965,0.539c0.289,0.167,0.574,0.34,0.857,0.52 c0.346,0.219,0.689,0.442,1.024,0.678c0.197,0.139,0.385,0.288,0.579,0.432c1.312,0.982,2.521,2.085,3.617,3.299 c0.067,0.074,0.134,0.146,0.199,0.219c0.381,0.433,0.746,0.878,1.098,1.337c0.032,0.042,0.064,0.085,0.095,0.125 c1.502,1.986,2.714,4.196,3.579,6.58c0,0,0.002,0.002,0.002,0.003c0,0,0,0.002,0,0.003c0.233,0.636,0.438,1.283,0.617,1.942 c0.025,0.087,0.044,0.174,0.067,0.261c0.154,0.589,0.296,1.179,0.405,1.783c0.05,0.268,0.084,0.542,0.125,0.813 c0.067,0.455,0.144,0.907,0.186,1.369c0.075,0.773,0.119,1.554,0.119,2.346c0,0.885-0.059,1.758-0.149,2.623 c-0.03,0.288-0.072,0.569-0.11,0.853c-0.082,0.584-0.179,1.159-0.301,1.732c-0.065,0.316-0.132,0.632-0.211,0.944 c-0.154,0.611-0.336,1.213-0.537,1.809c-0.074,0.223-0.13,0.448-0.212,0.669c0,0,0,0,0,0.002c-0.318,0.862-0.676,1.708-1.089,2.535 l-44.302,88.028c-3.917,7.78-3.518,17.033,1.051,24.448c4.569,7.415,12.656,11.929,21.366,11.929h22.976l108.706-0.002 c32.288,0,58.556,26.268,58.556,58.573l0.022,33.591c0,0.574,0.02,1.143,0.059,1.708l-0.03,38.615L423.342,391.124z"></path>
                    </g>
                    <path style="fill:#EFC27B;" d="M256.001,461.809h108.706c32.288,0,58.556-26.266,58.556-58.556l0.008-10.241 c0-0.634,0.025-1.265,0.072-1.889l-0.03-47.783l0.03-38.615c-0.038-0.565-0.059-1.134-0.059-1.707l-0.022-33.591 c0-32.304-26.268-58.572-58.556-58.572l-108.706,0.002V461.809z"></path>
                    <g>
                      <path style="fill:#ECB45C;" d="M214.133,461.809h41.866V210.858h-22.976c-8.708,0-16.795-4.514-21.365-11.929 c-4.567-7.417-4.967-16.668-1.051-24.448l44.302-88.028c0.413-0.825,0.773-1.673,1.089-2.535V66.765 c0-0.002-0.002-0.003-0.002-0.003c-0.865-2.381-2.078-4.594-3.579-6.58c-0.032-0.042-0.062-0.084-0.095-0.125 c-0.35-0.458-0.718-0.903-1.098-1.337c-0.065-0.074-0.132-0.146-0.199-0.219c-1.092-1.215-2.309-2.315-3.617-3.299 c-0.192-0.144-0.381-0.294-0.579-0.432c-0.335-0.238-0.679-0.458-1.024-0.678c-0.283-0.177-0.567-0.353-0.857-0.519 c-0.318-0.186-0.639-0.368-0.965-0.539c-0.38-0.199-0.771-0.383-1.163-0.56c-0.261-0.12-0.517-0.249-0.783-0.363 c-0.651-0.269-1.313-0.512-1.989-0.728c-0.231-0.074-0.468-0.132-0.701-0.199c-0.497-0.142-0.999-0.276-1.506-0.388 c-0.264-0.057-0.53-0.109-0.798-0.161c-0.517-0.097-1.039-0.174-1.568-0.241c-0.243-0.03-0.482-0.064-0.726-0.087 c-0.768-0.07-1.543-0.117-2.329-0.117c-9.575,0-18.184,5.325-22.474,13.896l-107.542,215.06c-0.033,0.065-0.065,0.13-0.099,0.194 c-9.061,17.734-13.655,36.909-13.655,56.993C88.657,405.522,144.946,461.809,214.133,461.809z"></path>
                    </g>
                  </g>
                </svg>
              </button>
              <button
                class="tcw-react-btn"
                :class="{
                  'tcw-reaction-shake': shakeReactions[`${msg.id}-heart`],
                }"
                data-reaction="heart"
                @click="() => toggleReactionWithShake(msg.id, 'heart')"
              >
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    <path d="M1.24264 8.24264L8 15L14.7574 8.24264C15.553 7.44699 16 6.36786 16 5.24264V5.05234C16 2.8143 14.1857 1 11.9477 1C10.7166 1 9.55233 1.55959 8.78331 2.52086L8 3.5L7.21669 2.52086C6.44767 1.55959 5.28338 1 4.05234 1C1.8143 1 0 2.8143 0 5.05234V5.24264C0 6.36786 0.44699 7.44699 1.24264 8.24264Z" fill="#ff0000"></path>
                  </g>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll to bottom button -->
    <div
      id="tcw-scroll-bottom"
      :class="{
        'tcw-hidden': !isScrolledUp || newMessagesWhileScrolled,
        'tcw-typing-mode': isScrolledUp && isAnyoneTyping,
      }"
    >
      <button
        :title="isAnyoneTyping ? 'Someone is typing...' : 'Scroll to bottom'"
        @click="scrollToBottom(true)"
      >
        <!-- Show typing dots icon when someone is typing -->
        <svg
          v-if="isAnyoneTyping"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            <path
              d="M18 12H18.01M12 12H12.01M6 12H6.01M13 12C13 12.5523 12.5523 13 12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12ZM19 12C19 12.5523 18.5523 13 18 13C17.4477 13 17 12.5523 17 12C17 11.4477 17.4477 11 18 11C18.5523 11 19 11.4477 19 12ZM7 12C7 12.5523 6.55228 13 6 13C5.44772 13 5 12.5523 5 12C5 11.4477 5.44772 11 6 11C6.55228 11 7 11.4477 7 12Z"
              stroke="#0040ff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </g>
        </svg>
        <!-- Show arrow icon when no one is typing -->
        <svg v-else viewBox="0 0 24 24">
          <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
      </button>
    </div>

    <!-- New messages button -->
    <div
      id="tcw-new-message-btn"
      :class="{ 'tcw-hidden': !newMessagesWhileScrolled }"
    >
      <button @click="scrollToBottom(true)">
        <svg viewBox="0 0 24 24">
          <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
        </svg>
        <span>New messages</span>
      </button>
    </div>

    <!-- Input area -->
    <div class="tcw-input-area">
      <!-- Reply context -->
      <div id="tcw-reply-context" :class="{ 'tcw-hidden': !replyingTo }">
        <button id="tcw-cancel-reply" @click="cancelReply">
          <svg viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <div class="tcw-reply-info">
          <div class="tcw-reply-label">Replying to:</div>
          <div id="tcw-reply-preview">
            {{
              replyingTo ? `${replyingTo.userName}: ${replyingTo.content}` : ""
            }}
          </div>
        </div>
      </div>

      <!-- Mention dropdown -->
      <div
        id="tcw-mention-dropdown"
        :class="{ 'tcw-hidden': !showMentionDropdown }"
      >
        <div
          v-if="showEveryone"
          class="tcw-mention-item tcw-mention-everyone"
          :data-username="'everyone'"
          :data-fullname="'@everyone'"
          @click="insertMention('everyone')"
        >
          <strong>@everyone</strong>
          <span style="color: #6c757d; font-size: 12px">
            ({{ filteredOnlineUsers.length }} online)</span
          >
        </div>
        <div
          v-for="user in mentionSuggestions"
          :key="user.userId"
          class="tcw-mention-item"
          :data-username="user.userName.split(' ')[0]"
          :data-fullname="user.userName"
          @click="insertMention(user.userName)"
        >
          {{ user.userName }}
        </div>
      </div>

      <!-- Emoji picker -->
      <div id="tcw-emoji-picker" :class="{ 'tcw-hidden': !showEmojiPicker }">
        <span
          v-for="emoji in emojis"
          :key="emoji"
          class="tcw-emoji-btn tcw-emoji"
          @click="addEmoji(emoji)"
          :title="getEmojiTitle(emoji)"
          >{{ emoji }}</span
        >
      </div>

      <!-- Input row -->
      <div class="tcw-input-row">
        <button id="tcw-emoji-toggle" title="Emojis" @click="toggleEmojiPicker">
          <svg viewBox="0 0 24 24" style="width: 20px; height: 20px">
            <circle cx="12" cy="12" r="10" fill="#FFD93D" />
            <circle cx="8.5" cy="10" r="1.5" fill="#1C1C1C" />
            <circle cx="15.5" cy="10" r="1.5" fill="#1C1C1C" />
            <path
              d="M8 14.5C8 14.5 9.5 17 12 17C14.5 17 16 14.5 16 14.5"
              stroke="#1C1C1C"
              stroke-width="1.5"
              stroke-linecap="round"
              fill="none"
            />
          </svg>
        </button>
        <input
          type="text"
          id="tcw-message-input"
          ref="messageInputRef"
          v-model="messageInput"
          :maxlength="config.maxMessageLength"
          placeholder="Type a message..."
          @keypress="handleKeyPress"
          @input="handleInputChange"
          @paste="handlePaste"
        />
        <button
          id="tcw-send-btn"
          title="Send"
          @click="sendMessage()"
          :disabled="sending"
        >
          <svg viewBox="0 0 24 24" style="width: 18px; height: 18px">
            <defs>
              <linearGradient
                id="sendGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style="stop-color: #667eea; stop-opacity: 1"
                />
                <stop
                  offset="100%"
                  style="stop-color: #764ba2; stop-opacity: 1"
                />
              </linearGradient>
            </defs>
            <path
              d="M2 21L23 12L2 3V10L17 12L2 14V21Z"
              fill="url(#sendGradient)"
            />
          </svg>
        </button>
      </div>

      <!-- Character count -->
      <div
        id="tcw-char-count"
        :class="{
          'tcw-hidden': messageInput.length < config.maxMessageLength * 0.9,
          'tcw-error': messageInput.length > config.maxMessageLength * 0.95,
        }"
      >
        {{ messageInput.length }} / {{ config.maxMessageLength }}
      </div>
    </div>

    <!-- Seen modal -->
    <Transition name="tcw-modal">
      <div v-if="seenModal.visible" id="tcw-seen-modal" @click.self="closeSeenModal">
        <div class="tcw-modal-content">
          <div class="tcw-modal-header">
            <h3>Seen by</h3>
            <button class="tcw-close-modal-btn" @click="closeSeenModal">
              <svg viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div id="tcw-seen-list">
            <div
              v-for="user in seenModal.users"
              :key="user.userId"
              class="tcw-seen-user"
            >
              <div class="tcw-user-avatar">
                {{ user.userName[0]?.toUpperCase() || "?" }}
              </div>
              <div class="tcw-seen-user-info">
                <span class="tcw-seen-user-name">{{ user.userName }}</span>
                <span
                  class="tcw-seen-user-time"
                  :title="formatExactSeenTime(user.seenAt)"
                >{{ formatSeenTime(user.seenAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Reaction modal -->
    <Transition name="tcw-modal">
      <div
        v-if="reactionModal.visible"
        id="tcw-reaction-modal"
        @click.self="closeReactionModal"
      >
        <div class="tcw-modal-content">
          <div class="tcw-modal-header">
            <h3>
              <span id="tcw-reaction-emoji">{{ reactionModal.emoji }}</span>
              Reactions
            </h3>
            <button class="tcw-close-reaction-modal" @click="closeReactionModal">
              <svg viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div id="tcw-reaction-list">
            <div
              v-for="userName in reactionModal.users"
              :key="userName"
              class="tcw-reaction-user"
            >
              <div class="tcw-user-avatar">
                {{ userName[0]?.toUpperCase() || "?" }}
              </div>
              <span>{{ userName }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <!-- Image viewer modal (separate from chat overlay) -->
  <div id="tcw-image-viewer" :class="{ 'tcw-hidden': !imageViewer.visible }">
    <div class="tcw-viewer-header">
      <button id="tcw-image-download" title="Download" @click="downloadImage">
        <svg viewBox="0 0 24 24">
          <path
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          ></path>
        </svg>
      </button>
      <button id="tcw-image-close" title="Close" @click="closeImageViewer">
        <svg viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
    <div class="tcw-image-container">
      <img
        id="tcw-viewer-image"
        :src="imageViewer.src"
        alt="Image"
        :style="{ transform: `scale(${imageZoomLevel})` }"
        :class="{ 'tcw-zoomed': imageZoomLevel > 1 }"
        @click="toggleImageZoom"
      />
      <div id="tcw-image-info">{{ imageViewer.info }}</div>
    </div>
    <div class="tcw-viewer-controls">
      <button id="tcw-zoom-in" title="Zoom In" @click="zoomIn">
        <svg viewBox="0 0 24 24">
          <path
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
          ></path>
        </svg>
      </button>
      <button id="tcw-zoom-out" title="Zoom Out" @click="zoomOut">
        <svg viewBox="0 0 24 24">
          <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"></path>
        </svg>
      </button>
      <button id="tcw-zoom-reset" title="Reset" @click="zoomReset">
        <svg viewBox="0 0 24 24">
          <path
            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
          ></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
///V 2.0
import {
  ref,
  reactive,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
} from "vue";
import emojiRegex from "emoji-regex";
import widgetStyles from "../../widget.css?inline";

/* =========================
   Props & Config
   ========================= */
const props = defineProps({
  apiKey: String,
  userName: String,
  userSurname: String,
  userId: String,
  wsUrl: String,
  cssHref: String,
  maxImageSize: Number,
  maxMessageLength: Number,
  compressionQuality: Number,
});

const config = reactive({
  apiKey: props.apiKey || "",
  userName: props.userName || "",
  userSurname: props.userSurname || "",
  userId: props.userId || "",
  wsUrl: props.wsUrl || "wss://office-chat.bestshop.ge/ws",
  maxImageSize: props.maxImageSize || 500000,
  maxMessageLength: props.maxMessageLength || 1000000,
  compressionQuality: props.compressionQuality || 0.8,
  cssHref: props.cssHref || "",
});

watch(
  () => props,
  (newProps) => {
    Object.keys(newProps).forEach((key) => {
      if (newProps[key] !== undefined) {
        config[key] = newProps[key];
      }
    });
  },
  { deep: true }
);

/* =========================
   Core State
   ========================= */
const isOpen = ref(false);
const messages = ref(new Map()); // key can be tempId or messageId (string)
const messageIdToKey = ref(new Map()); // Map<realMessageId(number|string), mapKey(string)>
const unreadCount = ref(0);
const onlineUsers = ref([]);
const lastMessageId = ref(0);
const lastSeenMessageId = ref(0);
const oldestMessageId = ref(null);
const hasMoreMessages = ref(true);
const loadingOldMessages = ref(false);
const isScrolledUp = ref(false);
const newMessagesWhileScrolled = ref(false);
const connectionStatus = ref("online");
const audioEnabled = ref(true);
const sending = ref(false);
const newlySentMessageIds = ref(new Set()); // Track newly sent messages for shake animation

/* =========================
   Activity & Visibility
   ========================= */
const isTabActive = ref(true);
const isTabVisible = ref(true);
const lastActivity = ref(Date.now());
const lastStatusUpdate = ref(0);
const statusUpdatePending = ref(false);

/* =========================
   Input & Mentions
   ========================= */
const messageInput = ref("");
const replyingTo = ref(null);
const showMentionDropdown = ref(false);
const mentionSuggestions = ref([]);
const mentionStartPos = ref(-1);
const showEmojiPicker = ref(false);
const shakeReactions = ref({});
const showEveryone = ref(false);

/* =========================
   Image Viewer
   ========================= */
const imageViewer = reactive({
  visible: false,
  src: "",
  info: "",
  filename: "",
});
const imageZoomLevel = ref(1);

/* =========================
   Modals
   ========================= */
const seenModal = reactive({
  visible: false,
  users: [],
});

const reactionModal = reactive({
  visible: false,
  emoji: "",
  users: [],
});

/* =========================
   Observer-based Seen Tracking
   ========================= */
const visibilityObserver = ref(null);
const observedMessages = ref(new Set());
const sendingSeenBatch = ref(false);
const pendingSeenIds = ref(new Set());

/* =========================
   Refs & Timers
   ========================= */
const messagesContainer = ref(null);
const messageInputRef = ref(null);

// WebSocket state
let ws = null;
let wsReconnectAttempts = 0;
let wsReconnectTimeout = null;
let isAuthenticated = ref(false);
let pendingMessages = [];

const failedMessages = new Map();
const retryTimeouts = new Map();

let activityTimeout = null;
let scrollTimeout = null;
let seenBatchTimer = null;

// Typing indicator state
let isCurrentlyTyping = false;
let typingTimeout = null;
const typingUsers = ref(new Map()); // Map<userId, {userName, timeout}>

const messagePreviews = ref({});

/* =========================
   Audio
   ========================= */
let messageSound = null;
let mentionSound = null;

/* =========================
   Scroll tracking
   ========================= */
let lastScrollTop = 0;
let scrollVelocity = 0;
let lastScrollTime = Date.now();

/* =========================
   Emojis
   ========================= */
const emojis = [
  "😀", // grinning face
  "😂", // face with tears of joy
  "😍", // smiling face with heart-eyes
  "🤔", // thinking face
  "😎", // smiling face with sunglasses
  "😊", // smiling face with smiling eyes
  "😢", // crying face
  "😅", // grinning face with sweat
  "🤣", // rolling on the floor laughing
  "😭", // loudly crying face
  "🥰", // smiling face with hearts
  "😴", // sleeping face
  "👍", // thumbs up
  "👎", // thumbs down
  "👏", // clapping hands
  "🙏", // folded hands
  "✌️", // victory hand
  "🤝", // handshake
  "❤️", // red heart
  "💔", // broken heart
  "🎉", // party popper
  "🔥", // fire
  "💯", // hundred points symbol
];

/* =========================
   Computed
   ========================= */
const sortedMessages = computed(() => {
  return Array.from(messages.value.values()).sort((a, b) => {
    const aIsTemp = typeof a.id === "string" && a.id.startsWith("temp_");
    const bIsTemp = typeof b.id === "string" && b.id.startsWith("temp_");
    if (aIsTemp && bIsTemp) {
      return new Date(a.timestamp) - new Date(b.timestamp);
    }
    const aId = aIsTemp ? Number.MAX_SAFE_INTEGER : parseInt(a.id, 10);
    const bId = bIsTemp ? Number.MAX_SAFE_INTEGER : parseInt(b.id, 10);
    if (aId === bId) {
      // tie-breaker by timestamp if needed
      return new Date(a.timestamp) - new Date(b.timestamp);
    }
    return aId - bId;
  });
});

const filteredOnlineUsers = computed(() =>
  onlineUsers.value.filter((u) => u.userId !== config.userId)
);

const isAnyoneTyping = computed(() => typingUsers.value.size > 0);

const replyPreview = computed(() => {
  if (!replyingTo.value) return "";
  return `${replyingTo.value.userName}: ${truncateReply(
    replyingTo.value.content
  )}`;
});

/* =========================
   Utilities
   ========================= */
async function fetchWithRetry(url, options, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        if (i < retries) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
          continue;
        }
        // After all retries, throw error for non-OK response
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response;
    } catch (error) {
      if (i === retries) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
}

function initAudio() {
  try {
    messageSound = new Audio(
      "https://cdnjs.cloudflare.com/ajax/libs/ion-sound/3.0.7/sounds/bell_ring.mp3"
    );
    mentionSound = new Audio(
      "https://cdnjs.cloudflare.com/ajax/libs/ion-sound/3.0.7/sounds/button_click.mp3"
    );
  } catch {
    // noop
  }
}

function getTypingText() {
  const users = Array.from(typingUsers.value.values());
  if (users.length === 0) return "";

  // Extract first name only (before space)
  const getFirstName = (fullName) => fullName.split(" ")[0];

  if (users.length === 1)
    return `${getFirstName(users[0].userName)} is typing...`;
  return `${users.length} people are typing...`;
}

function formatTime(timestamp) {
  let date;
  if (typeof timestamp === "string") {
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timestamp)) {
      date = new Date(timestamp.replace(" ", "T") + "Z");
    } else {
      date = new Date(timestamp);
    }
  } else {
    date = new Date(timestamp);
  }
  if (isNaN(date.getTime())) return "Invalid date";

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  const minutesStr = minutes < 10 ? "0" + minutes : minutes;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const dayDiff = Math.floor((today - messageDay) / (1000 * 60 * 60 * 24));

  if (dayDiff === 0) return `${hour12}:${minutesStr} ${ampm}`;
  if (dayDiff === 1) return `Yesterday ${hour12}:${minutesStr} ${ampm}`;
  if (dayDiff < 7) {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return `${days[date.getDay()]} ${hour12}:${minutesStr} ${ampm}`;
  }
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day} ${hour12}:${minutesStr} ${ampm}`;
}

function escapeHtml(text) {
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return String(text || "").replace(/[&<>"']/g, (m) => map[m]);
}

// Enhanced function to detect if message contains only emojis (and whitespace)
function isEmojiOnlyMessage(content) {
  const regex = emojiRegex();
  const trimmed = content.trim();

  // Remove all emojis and see if anything meaningful remains
  const withoutEmojis = trimmed.replace(regex, "").replace(/\s+/g, "");

  // Must have at least one emoji and nothing else meaningful
  const hasEmojis = regex.test(trimmed);
  return hasEmojis && withoutEmojis.length === 0;
}

// Function to detect if message contains exactly one emoji and nothing else
function isSingleEmojiMessage(content) {
  const regex = emojiRegex();
  const trimmed = content.trim();

  // Remove all whitespace to check for single emoji
  const noWhitespace = trimmed.replace(/\s+/g, "");

  // Find all emoji matches
  const matches = Array.from(noWhitespace.matchAll(regex));

  // Check if there's exactly one emoji and nothing else
  return matches.length === 1 && noWhitespace === matches[0][0];
}

function processMentions(content) {
  let processed = escapeHtml(content);
  const myFullName = `${config.userName} ${config.userSurname}`;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const isEmojiOnly = isEmojiOnlyMessage(content);

  // Phone number regex - matches various formats
  // 5XXXXXXXX, +995XXXXXXXXX, (5XX) XXX-XXXX, etc.
  const phoneRegex =
    /(?:\+?\d{1,3}[\s\-]?)?\(?(?:5\d{8}|\d{3})\)?[\s\-]?\d{3}[\s\-]?\d{4}|5\d{8}/g;

  // Process @everyone mentions
  processed = processed.replace(
    /@everyone\b/g,
    '<span class="tcw-mention-everyone-text">@everyone</span>'
  );

  // Process user mentions
  processed = processed.replace(/@(\S+)/g, (match) => {
    if (match === "@everyone") return match;
    if (match === `@${myFullName}` || match === `@${config.userName}`) {
      return `<span class="tcw-mention-me">${match}</span>`;
    }
    return `<span class="tcw-mention">${match}</span>`;
  });

  // Process URLs - convert them to links and fetch previews
  processed = processed.replace(urlRegex, (url) => {
    // Fetch preview asynchronously (fire and forget)
    fetchLinkPreview(url);

    // Return the link HTML
    return `<a href="${url}" target="_blank" class="tcw-link">${url}</a>`;
  });

  // Process phone numbers - convert them to clickable tel: links
  processed = processed.replace(phoneRegex, (phone) => {
    // Clean phone number for tel: link (remove spaces, dashes, parentheses)
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");

    // Format display version (keep original formatting)
    const displayPhone = phone.trim();

    // Add + prefix if it doesn't exist and looks like international number
    const telLink = cleanPhone.startsWith("+")
      ? cleanPhone
      : cleanPhone.length > 9
      ? `+${cleanPhone}`
      : cleanPhone;

    return `<a href="tel:${telLink}" class="tcw-phone-link" title="Call ${displayPhone}">${displayPhone}</a>`;
  });

  // Process emojis for consistent rendering and enlargement
  const emojiRegexPattern = emojiRegex();
  processed = processed.replace(emojiRegexPattern, (emoji) => {
    const emojiClass = isEmojiOnly ? "tcw-emoji tcw-emoji-large" : "tcw-emoji";
    return `<span class="${emojiClass}" role="img" aria-label="emoji">${emoji}</span>`;
  });

  return processed;
}

// Add this new function after processMentions
async function fetchLinkPreview(url) {
  // Skip if we already tried (successful or failed)
  if (messagePreviews.value[url] !== undefined) return;

  // Mark as attempted immediately
  messagePreviews.value[url] = null;

  try {
    const response = await fetch(
      `https://best-shop.ge/linkpreview/?q=${encodeURIComponent(url)}`
    );

    if (!response.ok) return;

    const data = await response.json();

    if (data && (data.title || data.description)) {
      messagePreviews.value[url] = {
        url: data.url || url,
        image: data.image || "",
        title: data.title || "",
        description: data.description || "",
      };
    }
  } catch (error) {
    console.error(`Failed to fetch link preview for ${url}:`, error);
    // messagePreviews[url] stays null, preventing future attempts
  }
}

function getMessageUrls(content) {
  if (!content) return [];
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = content.match(urlRegex);
  return matches || [];
}

function isMentioned(msg) {
  if (msg.userId === config.userId) return false;
  const myFullName = `${config.userName} ${config.userSurname}`;
  const content = msg.content || "";
  return (
    content.includes("@everyone") ||
    content.includes(`@${myFullName}`) ||
    content.includes(`@${config.userName}`)
  );
}

function truncateReply(content) {
  if (!content) return "[Image]";
  return content.length > 50 ? content.slice(0, 50) + "..." : content;
}

function getSeenCount(msg) {
  if (!msg.seen) return 0;
  return msg.seen.filter((u) => u.userId !== config.userId).length;
}

function getSeenUserNames(msg) {
  if (!msg.seen) return "";
  return msg.seen
    .filter((u) => u.userId !== config.userId)
    .map((u) => u.userName)
    .join(",");
}

function formatSeenTime(seenAt) {
  if (!seenAt) return "";

  // Convert UTC timestamp to local time (same pattern as formatTime)
  const seenDate = new Date(seenAt.replace(' ', 'T') + 'Z');
  const now = new Date();
  const diffMs = now - seenDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  // For older dates, show the actual date
  return seenDate.toLocaleDateString();
}

function formatExactSeenTime(seenAt) {
  if (!seenAt) return "";

  // Convert UTC timestamp to local time (same pattern as formatTime)
  const seenDate = new Date(seenAt.replace(' ', 'T') + 'Z');
  return seenDate.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function getReactionUserNames(msg, type) {
  if (!msg.reactions || !msg.reactions[type]) return "";
  return msg.reactions[type].map((u) => u.userName).join(",");
}

function hasReactions(msg) {
  return (
    msg.reactions &&
    ((msg.reactions.thumbsUp && msg.reactions.thumbsUp.length > 0) ||
      (msg.reactions.heart && msg.reactions.heart.length > 0))
  );
}

function hasMyReaction(msg, type) {
  if (!msg.reactions || !msg.reactions[type]) return false;
  return msg.reactions[type].some((u) => u.userId === config.userId);
}

/* =========================
   Map helpers
   ========================= */
function setMessageInStore(key, msg) {
  messages.value.set(String(key), msg);
  // Index by real messageId if available
  if (msg.id && typeof msg.id !== "string") {
    messageIdToKey.value.set(Number(msg.id), String(key));
  } else if (typeof msg.id === "string" && !msg.id.startsWith("temp_")) {
    // numeric as string
    const n = Number(msg.id);
    if (!Number.isNaN(n)) {
      messageIdToKey.value.set(n, String(key));
    }
  }
}

function getByMessageId(messageId) {
  const midNum = Number(messageId);
  const keyByIndex =
    messageIdToKey.value.get(midNum) ||
    messageIdToKey.value.get(String(messageId));
  if (keyByIndex && messages.value.has(keyByIndex)) {
    return { msg: messages.value.get(keyByIndex), key: keyByIndex };
  }
  if (messages.value.has(String(messageId))) {
    return {
      msg: messages.value.get(String(messageId)),
      key: String(messageId),
    };
  }
  if (messages.value.has(Number(messageId))) {
    return {
      msg: messages.value.get(Number(messageId)),
      key: String(Number(messageId)),
    };
  }
  return { msg: null, key: null };
}

/* =========================
   Observer-based Seen Tracking
   ========================= */
function shouldMarkAsSeen() {
  return isOpen.value && isTabActive.value && isTabVisible.value;
}

function initVisibilityTracking() {
  if (!("IntersectionObserver" in window)) {
    console.warn(
      "IntersectionObserver not supported, falling back to basic seen tracking"
    );
    return;
  }

  visibilityObserver.value = new IntersectionObserver(
    (entries) => {
      if (!shouldMarkAsSeen()) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const messageId = entry.target.dataset.messageId;
          if (!messageId) return;

          const { msg } = getByMessageId(messageId);

          if (msg && msg.userId !== config.userId && !msg.seenByMe) {
            msg.seenByMe = true;
            pendingSeenIds.value.add(Number(msg.id));
            observedMessages.value.delete(String(messageId));
            visibilityObserver.value.unobserve(entry.target);

            // Batch send seen updates
            scheduleSeenBatch();
          }
        }
      });
    },
    {
      root: messagesContainer.value,
      threshold: 0.5, // Message is 50% visible
    }
  );
}

function scheduleSeenBatch() {
  if (seenBatchTimer) clearTimeout(seenBatchTimer);

  seenBatchTimer = setTimeout(() => {
    if (pendingSeenIds.value.size > 0) {
      sendSeenBatch(Array.from(pendingSeenIds.value));
      pendingSeenIds.value.clear();
    }
  }, 500); // Wait 500ms to batch multiple seen updates
}

async function sendSeenBatch(messageIds) {
  if (!messageIds.length || sendingSeenBatch.value) return;

  sendingSeenBatch.value = true;

  // Send via WebSocket
  wsSend({
    action: "markSeen",
    messageIds,
  });

  sendingSeenBatch.value = false;
}

function observeMessage(element, messageId) {
  if (!visibilityObserver.value || !element) return;

  const { msg } = getByMessageId(messageId);
  if (
    msg &&
    msg.userId !== config.userId &&
    !msg.seenByMe &&
    typeof msg.id !== "string"
  ) {
    observedMessages.value.add(String(messageId));
    visibilityObserver.value.observe(element);
  }
}

function reobserveUnseenMessages() {
  if (!shouldMarkAsSeen() || !visibilityObserver.value) return;

  nextTick(() => {
    const container = messagesContainer.value;
    if (!container) return;

    messages.value.forEach((msg) => {
      if (
        msg.userId !== config.userId &&
        !msg.seenByMe &&
        typeof msg.id !== "string"
      ) {
        const element = container.querySelector(
          `[data-message-id="${msg.id}"]`
        );
        if (element && !observedMessages.value.has(String(msg.id))) {
          observeMessage(element, msg.id);
        }
      }
    });
  });
}

/* =========================
   UI Actions
   ========================= */
function toggle() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    unreadCount.value = 0;
    nextTick(() => {
      scrollToBottom(false);
      messageInputRef.value?.focus();
      // Observer will handle seen tracking automatically
      reobserveUnseenMessages();
    });
  }
}

function handleScroll() {
  if (!messagesContainer.value) return;

  const currentTime = Date.now();
  const el = messagesContainer.value;
  const scrollTop = el.scrollTop;
  const scrollHeight = el.scrollHeight;
  const clientHeight = el.clientHeight;

  const timeDelta = currentTime - lastScrollTime;
  const scrollDelta = scrollTop - lastScrollTop;
  scrollVelocity = timeDelta > 0 ? scrollDelta / timeDelta : 0;

  lastScrollTop = scrollTop;
  lastScrollTime = currentTime;

  const isAtBottom = scrollHeight - scrollTop - clientHeight <= 50;
  isScrolledUp.value = !isAtBottom;

  if (isAtBottom) {
    newMessagesWhileScrolled.value = false;
    // Observer will handle seen tracking automatically
  }

  // Load older messages near top
  if (
    scrollTop < 300 &&
    scrollVelocity <= 0.5 &&
    !loadingOldMessages.value &&
    hasMoreMessages.value
  ) {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const currentScrollTop = messagesContainer.value?.scrollTop ?? 0;
      if (
        currentScrollTop < 300 &&
        !loadingOldMessages.value &&
        hasMoreMessages.value
      ) {
        loadOlderMessages();
      }
    }, 150);
  } else {
    clearTimeout(scrollTimeout);
  }
}

function scrollToBottom(animate = true) {
  nextTick(() => {
    nextTick(() => {
      setTimeout(() => {
        if (!messagesContainer.value) return;
        const el = messagesContainer.value;
        const targetScroll = el.scrollHeight - el.clientHeight;

        if (animate) {
          const start = el.scrollTop;
          const end = targetScroll;
          const duration = 300;
          const startTime = Date.now();

          const animateScroll = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const ease =
              progress < 0.5
                ? 2 * progress * progress
                : -1 + (4 - 2 * progress) * progress;

            el.scrollTop = start + (end - start) * ease;
            if (progress < 1) requestAnimationFrame(animateScroll);
          };
          requestAnimationFrame(animateScroll);
        } else {
          el.scrollTop = targetScroll;
        }

        isScrolledUp.value = false;
        newMessagesWhileScrolled.value = false;
      }, 50); // Small delay to ensure typing indicator is rendered
    });
  });
}

function handleKeyPress(e) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
}

/* =========================
   Send Message (Correct Logic)
   ========================= */
async function sendMessage(content, retryData) {
  let message;

  if (content && typeof content === "object" && content.target) {
    content = undefined;
  }

  if (retryData?.messageData) {
    message = retryData.messageData.content;
  } else if (typeof content === "string") {
    message = content;
  } else {
    message = messageInput.value.trim();
  }

  if (!message && !retryData) return;

  if (message.length > config.maxMessageLength) {
    alert(
      `Message is too long. Maximum ${config.maxMessageLength} characters.`
    );
    return;
  }

  sending.value = true;
  const tempId = retryData
    ? retryData.tempId
    : `temp_${Date.now()}_${Math.random()}`;

  // Expand @everyone to back-end list (if needed)
  let mentionedUsers = [];
  if (message.includes("@everyone") && !retryData) {
    mentionedUsers = filteredOnlineUsers.value.map((u) => ({
      userId: u.userId,
      userName: u.userName,
    }));
  }

  const messageData = retryData
    ? retryData.messageData
    : {
        action: "message",
        tempId, // include tempId for backend echo
        userId: config.userId,
        userName: `${config.userName} ${config.userSurname}`,
        content: message,
        timestamp: new Date().toISOString().slice(0, 19).replace("T", " "),
        type:
          typeof content === "string" && content.startsWith("data:image")
            ? "image"
            : "text",
        replyTo: replyingTo.value ? replyingTo.value : null,
        mentionedUsers: mentionedUsers.length ? mentionedUsers : undefined,
      };

  // Create local message with tempId as its ID; Map key = tempId
  if (!retryData) {
    const localMessage = {
      id: tempId,
      userId: config.userId,
      userName: `${config.userName} ${config.userSurname}`,
      content: message,
      timestamp: messageData.timestamp,
      type: messageData.type,
      replyTo: messageData.replyTo,
      seen: [],
      reactions: { thumbsUp: [], heart: [] },
      status: "sending",
    };
    setMessageInStore(tempId, localMessage); // index is only by temp key for now

    // Track newly sent message for shake animation (store timestamp)
    newlySentMessageIds.value.add(tempId);

    messageInput.value = "";
    replyingTo.value = null;
    scrollToBottom();
  } else {
    const existing = messages.value.get(tempId);
    if (existing) existing.status = "sending";
  }

  failedMessages.set(tempId, messageData);

  // Clear typing status when sending message
  if (isCurrentlyTyping) {
    wsSend({ action: "typing", isTyping: false });
    isCurrentlyTyping = false;
    clearTimeout(typingTimeout);
  }

  // Send via WebSocket
  const wsMessage = {
    action: "sendMessage",
    content: message,
    type: messageData.type,
    tempId,
    replyTo: messageData.replyTo,
    mentionedUsers: messageData.mentionedUsers,
  };

  if (wsSend(wsMessage)) {
    sending.value = false;
    return;
  } else {
    // WebSocket not connected, mark as failed
    const msg = messages.value.get(tempId);
    if (msg) msg.status = "failed";
    sending.value = false;
    return;
  }
}

function retryMessage(msg) {
  const tempKey =
    typeof msg.id === "string" && msg.id.startsWith("temp_")
      ? msg.id
      : messageIdToKey.value.get(Number(msg.id));
  const key = tempKey || msg.id;
  if (failedMessages.has(key)) {
    sendMessage(null, {
      tempId: key,
      messageData: failedMessages.get(key),
      attemptCount: 0,
    });
  }
}

/* =========================
   Store Merge/Insert
   ========================= */
function addMessage(message, playSound = true, prepend = false) {
  // If message has real id, try deduplicate via index
  if (message.id && typeof message.id !== "string") {
    const mid = Number(message.id);
    const existingKey = messageIdToKey.value.get(mid);
    if (existingKey && messages.value.has(existingKey)) {
      return; // already have it
    }
  }

  // Determine key for storage:
  // - For real messages from others: use messageId as key (string)
  // - For our local temp messages: they already exist; this path is mainly for fetched messages
  const key =
    typeof message.id === "string" && message.id.startsWith("temp_")
      ? message.id
      : String(message.id);

  if (messages.value.has(key)) {
    // Merge into existing object (keep reference)
    const existing = messages.value.get(key);
    Object.assign(existing, message);
  } else {
    setMessageInStore(key, message);
  }

  const isOwnMessage = message.userId === config.userId;

  if (prepend) {
    if (
      !oldestMessageId.value ||
      Number(message.id) < Number(oldestMessageId.value)
    ) {
      oldestMessageId.value = Number(message.id);
    }
  } else {
    if (isOpen.value) {
      const el = messagesContainer.value;
      if (el) {
        const distanceFromBottom =
          el.scrollHeight - el.scrollTop - el.clientHeight;
        const isNearBottom = distanceFromBottom <= 200;
        if (isOwnMessage || isNearBottom) {
          nextTick(() => {
            scrollToBottom(false);
            // After DOM update, observe new messages
            reobserveUnseenMessages();
          });
        } else {
          newMessagesWhileScrolled.value = true;
        }
      }
    }
  }

  // Sounds
  if (!isOwnMessage && playSound && !prepend) {
    const mentioned = isMentioned(message);
    if (mentioned) {
      if (!isOpen.value) toggle();
      if (audioEnabled.value && mentionSound) {
        try {
          mentionSound.play();
        } catch {}
      }
      if ("Notification" in window && Notification.permission === "granted") {
        const mentionType = message.content?.includes("@everyone")
          ? " (everyone)"
          : "";
        new Notification("New mention" + mentionType, {
          body: `${message.userName}: ${String(message.content || "").slice(
            0,
            100
          )}`,
          icon: "/favicon.ico",
        });
      }
    } else if (!isOpen.value && audioEnabled.value && messageSound) {
      try {
        messageSound.play();
      } catch {}
    }
  }

  // After adding a new message, observe it if needed
  if (!prepend && !isOwnMessage) {
    nextTick(() => reobserveUnseenMessages());
  }
}

/* =========================
   Fetchers
   ========================= */

async function loadOlderMessages() {
  if (loadingOldMessages.value || !hasMoreMessages.value) return;

  loadingOldMessages.value = true;

  let beforeId = oldestMessageId.value;
  if (!beforeId && messages.value.size > 0) {
    const sorted = sortedMessages.value;
    beforeId = sorted[0]?.id;
    oldestMessageId.value = beforeId;
  }
  if (!beforeId) {
    loadingOldMessages.value = false;
    return;
  }

  // Load via WebSocket
  wsSend({
    action: "loadHistory",
    before: Number(beforeId),
    limit: 20,
  });
  // Loading state will be cleared by handleMessageHistory
}

/* =========================
   Reactions
   ========================= */
async function toggleReaction(messageIdOrKey, reaction) {
  // Resolve message by messageId if possible
  let msg = null;
  let key = null;

  if (
    typeof messageIdOrKey === "string" &&
    messageIdOrKey.startsWith("temp_")
  ) {
    // cannot react to un-acked temp message from others (we only show reactions for others anyway)
    return;
  }

  const { msg: found, key: k } = getByMessageId(messageIdOrKey);
  if (found) {
    msg = found;
    key = k;
  } else if (messages.value.has(String(messageIdOrKey))) {
    msg = messages.value.get(String(messageIdOrKey));
    key = String(messageIdOrKey);
  }

  if (!msg) return;

  if (!msg.reactions) msg.reactions = { thumbsUp: [], heart: [] };

  const otherReaction = reaction === "thumbsUp" ? "heart" : "thumbsUp";

  // Remove from other reaction if exists
  const otherIdx = msg.reactions[otherReaction].findIndex(
    (u) => u.userId === config.userId
  );
  if (otherIdx > -1) msg.reactions[otherReaction].splice(otherIdx, 1);

  // Toggle current reaction
  const myIdx = msg.reactions[reaction].findIndex(
    (u) => u.userId === config.userId
  );
  const isRemoving = myIdx > -1;
  if (isRemoving) {
    msg.reactions[reaction].splice(myIdx, 1);
  } else {
    msg.reactions[reaction].push({
      userId: config.userId,
      userName: `${config.userName} ${config.userSurname}`,
    });
  }

  // Persist via WebSocket
  if (msg.id && String(msg.id).indexOf("temp_") !== 0) {
    if (isRemoving) {
      wsSend({
        action: "removeReaction",
        messageId: Number(msg.id),
      });
    } else {
      wsSend({
        action: "addReaction",
        messageId: Number(msg.id),
        reaction,
      });
    }
  }
}

// Wrapper function to add subtle shake animation to reaction clicks
function toggleReactionWithShake(messageId, reaction) {
  // Trigger the original reaction toggle
  toggleReaction(messageId, reaction);

  // Add subtle shake animation using Vue reactivity
  const shakeKey = `${messageId}-${reaction}`;
  shakeReactions.value[shakeKey] = true;

  // Remove shake class after animation completes
  setTimeout(() => {
    shakeReactions.value[shakeKey] = false;
  }, 200);
}

/* =========================
   Reply
   ========================= */
function setReplyTo(msg) {
  replyingTo.value = {
    id: msg.id,
    userName: msg.userName,
    content: msg.type === "image" ? "[Image]" : msg.content,
  };
  nextTick(() => messageInputRef.value?.focus());
}
function cancelReply() {
  replyingTo.value = null;
}

/* =========================
   Mentions
   ========================= */
function handleInputChange() {
  const text = messageInput.value;
  const cursorPos = messageInputRef.value?.selectionStart || 0;

  const lastAt = text.lastIndexOf("@", cursorPos - 1);
  if (lastAt !== -1 && cursorPos > lastAt) {
    const searchText = text.substring(lastAt + 1, cursorPos).toLowerCase();
    if (!searchText.includes(" ")) {
      mentionStartPos.value = lastAt;
      showMentionSuggestions(searchText);
    } else {
      hideMentionSuggestions();
    }
  } else {
    hideMentionSuggestions();
  }

  // Handle typing indicator
  handleTypingIndicator();
}

// Typing indicator with throttling
function handleTypingIndicator() {
  // Only send if authenticated and connected
  if (!isAuthenticated.value || !ws || ws.readyState !== WebSocket.OPEN) {
    return;
  }

  const hasText = messageInput.value.trim().length > 0;

  // Send typing=true only once when user starts typing
  if (hasText && !isCurrentlyTyping) {
    wsSend({ action: "typing", isTyping: true });
    isCurrentlyTyping = true;
  }

  // Reset timeout - will send typing=false after 2s of inactivity
  clearTimeout(typingTimeout);

  if (hasText) {
    typingTimeout = setTimeout(() => {
      wsSend({ action: "typing", isTyping: false });
      isCurrentlyTyping = false;
    }, 2000); // 2 seconds of inactivity
  } else {
    // If input is empty, immediately stop typing
    if (isCurrentlyTyping) {
      wsSend({ action: "typing", isTyping: false });
      isCurrentlyTyping = false;
    }
  }
}

function showMentionSuggestions(searchText) {
  const suggestions = filteredOnlineUsers.value.filter((u) =>
    u.userName.toLowerCase().includes(searchText)
  );
  mentionSuggestions.value = suggestions;
  showEveryone.value = searchText === "" || "everyone".includes(searchText);
  showMentionDropdown.value = suggestions.length > 0 || showEveryone.value;
}

function hideMentionSuggestions() {
  showMentionDropdown.value = false;
  mentionStartPos.value = -1;
}

function insertMention(userName) {
  const text = messageInput.value;
  const cursorPos = messageInputRef.value?.selectionStart || text.length;

  if (mentionStartPos.value !== -1) {
    const newText =
      text.substring(0, mentionStartPos.value) +
      "@" +
      userName +
      " " +
      text.substring(cursorPos);
    messageInput.value = newText;
    const newCursorPos = mentionStartPos.value + userName.length + 2;
    nextTick(() => {
      messageInputRef.value?.setSelectionRange(newCursorPos, newCursorPos);
    });
  }
  hideMentionSuggestions();
  messageInputRef.value?.focus();
}

function insertMentionFromOnlineUser(userName) {
  const text = messageInput.value;
  const newText =
    (text && !text.endsWith(" ") ? text + " " : text) + "@" + userName + " ";
  messageInput.value = newText;
  messageInputRef.value?.focus();
}

/* =========================
   Emoji
   ========================= */
function toggleEmojiPicker() {
  showEmojiPicker.value = !showEmojiPicker.value;
}
function addEmoji(emoji) {
  messageInput.value += emoji;
  showEmojiPicker.value = false;
  messageInputRef.value?.focus();
}

// Get emoji description for tooltip
function getEmojiTitle(emoji) {
  const emojiTitles = {
    "😀": "Grinning Face",
    "😂": "Face with Tears of Joy",
    "😍": "Smiling Face with Heart-Eyes",
    "🤔": "Thinking Face",
    "😎": "Smiling Face with Sunglasses",
    "😊": "Smiling Face with Smiling Eyes",
    "😢": "Crying Face",
    "😅": "Grinning Face with Sweat",
    "🤣": "Rolling on the Floor Laughing",
    "😭": "Loudly Crying Face",
    "🥰": "Smiling Face with Hearts",
    "😴": "Sleeping Face",
    "👍": "Thumbs Up",
    "👎": "Thumbs Down",
    "👏": "Clapping Hands",
    "🙏": "Folded Hands",
    "✌️": "Victory Hand",
    "🤝": "Handshake",
    "❤️": "Red Heart",
    "💔": "Broken Heart",
    "🎉": "Party Popper",
    "🔥": "Fire",
    "💯": "Hundred Points Symbol",
  };
  return emojiTitles[emoji] || "Emoji";
}

/* =========================
   Images
   ========================= */
function handlePaste(event) {
  const items = event.clipboardData?.items || [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type && items[i].type.indexOf("image") !== -1) {
      const blob = items[i].getAsFile();
      handleImagePaste(blob);
      event.preventDefault();
      break;
    }
  }
}

function handleImagePaste(blob) {
  if (!blob) return;
  if (blob.size > config.maxImageSize * 2) {
    alert(
      `Image is too large. Maximum size is about ${Math.round(
        config.maxImageSize / 1000
      )}KB.`
    );
    return;
  }
  compressImage(blob, config.compressionQuality, (compressedBlob) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const imgData = e.target.result;
      if (String(imgData).length > config.maxImageSize) {
        alert(
          "Compressed image is still too large. Please use a smaller image."
        );
        return;
      }
      if (confirm("Send this image?")) {
        sendMessage(imgData);
      }
    };
    reader.readAsDataURL(compressedBlob);
  });
}

function compressImage(blob, quality, callback) {
  const img = new Image();
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  img.onload = () => {
    const maxWidth = 1200;
    const maxHeight = 1200;
    let width = img.width;
    let height = img.height;

    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width *= ratio;
      height *= ratio;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob(callback, "image/jpeg", quality);
  };
  img.src = URL.createObjectURL(blob);
}

/* =========================
   Image Viewer
   ========================= */
function openImageViewer(msg) {
  imageViewer.visible = true;
  imageViewer.src = msg.content;
  imageViewer.info = `${msg.userName} • ${formatTime(msg.timestamp)}`;
  imageViewer.filename = `chat-image-${Date.now()}.png`;
  imageZoomLevel.value = 1;
  document.body.style.overflow = "hidden";
}
function closeImageViewer() {
  imageViewer.visible = false;
  document.body.style.overflow = "";
}
function toggleImageZoom() {
  imageZoomLevel.value = imageZoomLevel.value === 1 ? 2 : 1;
}
function downloadImage() {
  const link = document.createElement("a");
  link.href = imageViewer.src;
  link.download = imageViewer.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
function zoomIn() {
  imageZoomLevel.value = Math.min(imageZoomLevel.value + 0.5, 3);
}
function zoomOut() {
  imageZoomLevel.value = Math.max(imageZoomLevel.value - 0.5, 0.5);
}
function zoomReset() {
  imageZoomLevel.value = 1;
}

/* =========================
   Modals
   ========================= */
function showSeenModal(msg) {
  const realId = typeof msg.id === "string" ? null : msg.id;
  if (!realId) return; // cannot show for temp messages

  if (msg.seen?.length) {
    const seenUsers = msg.seen
      .filter((user) => user.userId !== config.userId);
    if (seenUsers.length > 0) {
      seenModal.users = seenUsers;
      seenModal.visible = true;
    }
  }
}
function closeSeenModal() {
  seenModal.visible = false;
}

function showReactionModal(msg, reaction) {
  const users = msg.reactions?.[reaction] || [];
  const names = users.map((u) => u.userName);
  if (names.length) {
    reactionModal.emoji = reaction === "thumbsUp" ? "👍" : "❤️";
    reactionModal.users = names;
    reactionModal.visible = true;
  }
}
function closeReactionModal() {
  reactionModal.visible = false;
}

/* =========================
   Sound & Connection
   ========================= */
function toggleSound() {
  audioEnabled.value = !audioEnabled.value;
}

function showConnectionStatus(status) {
  connectionStatus.value = status;
  if (status === "online") {
    // Retry failed messages automatically
    failedMessages.forEach((messageData, tempId) => {
      if (!retryTimeouts.has(tempId)) {
        sendMessage(null, { tempId, messageData, attemptCount: 0 });
      }
    });
  }
}

/* =========================
   WebSocket Connection
   ========================= */
function connectWebSocket() {
  // Prevent multiple simultaneous connection attempts
  if (
    ws &&
    (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)
  ) {
    console.log("WebSocket already connected or connecting");
    return;
  }

  // Clean up existing connection
  if (ws) {
    ws.close();
    ws = null;
  }

  connectionStatus.value = "connecting";
  isAuthenticated.value = false;

  try {
    ws = new WebSocket(config.wsUrl);

    ws.onopen = () => {
      console.log("WebSocket connected, authenticating...");
      wsReconnectAttempts = 0;

      // Authenticate immediately
      wsSend({
        action: "authenticate",
        userId: config.userId,
        userName: `${config.userName} ${config.userSurname}`,
      });
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      connectionStatus.value = "offline";
    };

    ws.onclose = () => {
      console.log("WebSocket closed");
      isAuthenticated.value = false;
      connectionStatus.value = "offline";

      // Auto-reconnect with exponential backoff
      const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts), 30000);
      wsReconnectAttempts++;

      clearTimeout(wsReconnectTimeout);
      wsReconnectTimeout = setTimeout(() => {
        connectWebSocket();
      }, delay);
    };
  } catch (error) {
    console.error("Failed to create WebSocket connection:", error);
    connectionStatus.value = "offline";
  }
}

function wsSend(data) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data));
    return true;
  } else {
    console.warn("WebSocket not connected, message queued");
    pendingMessages.push(data);
    return false;
  }
}

function handleWebSocketMessage(data) {
  switch (data.type) {
    case "authenticated":
      handleAuthenticated(data);
      break;
    case "newMessage":
      handleNewMessage(data.message);
      break;
    case "messageSent":
      handleMessageSent(data);
      break;
    case "seenUpdate":
      handleSeenUpdate(data);
      break;
    case "seenConfirmed":
      handleSeenConfirmed(data);
      break;
    case "reactionUpdate":
      handleReactionUpdate(data);
      break;
    case "userStatusUpdate":
      handleUserStatusUpdate(data);
      break;
    case "userTyping":
      handleUserTyping(data);
      break;
    case "messageHistory":
      handleMessageHistory(data);
      break;
    case "error":
      console.error("WebSocket error from server:", data.error);
      break;
    case "pong":
      // Heartbeat response
      break;
    default:
      console.warn("Unknown WebSocket message type:", data.type);
  }
}

function handleAuthenticated(data) {
  console.log("WebSocket authenticated");
  isAuthenticated.value = true;
  connectionStatus.value = "online";
  wsReconnectAttempts = 0;

  // Process initial data
  if (data.messages && Array.isArray(data.messages)) {
    data.messages.forEach((msg) => {
      addMessage(msg, false);
    });
    nextTick(() => scrollToBottom(false));
  }

  if (typeof data.unreadCount === "number") {
    unreadCount.value = data.unreadCount;
  }

  if (data.onlineUsers && Array.isArray(data.onlineUsers)) {
    onlineUsers.value = data.onlineUsers;
  }

  // Send any pending messages
  if (pendingMessages.length > 0) {
    pendingMessages.forEach((msg) => wsSend(msg));
    pendingMessages = [];
  }
}

function handleNewMessage(message) {
  if (!message) return;

  // Add message to UI
  addMessage(message, true);

  // Update last message ID
  if (message.id > lastMessageId.value) {
    lastMessageId.value = message.id;
  }
}

function handleMessageSent(data) {
  const { message, tempId } = data;

  if (tempId && message) {
    // Replace temp message with real one
    const tmp = messages.value.get(tempId);
    if (tmp) {
      tmp.id = Number(message.id);
      delete tmp.status;
      messageIdToKey.value.set(Number(message.id), tempId);

      // Update shake animation tracking with real ID
      if (newlySentMessageIds.value.has(tempId)) {
        newlySentMessageIds.value.delete(tempId);
        newlySentMessageIds.value.add(Number(message.id));

        // Remove from tracking after 500ms
        setTimeout(() => {
          newlySentMessageIds.value.delete(Number(message.id));
        }, 500);
      }
    }

    if (message.id > lastMessageId.value) {
      lastMessageId.value = message.id;
    }

    failedMessages.delete(tempId);
    if (retryTimeouts.has(tempId)) {
      clearTimeout(retryTimeouts.get(tempId));
      retryTimeouts.delete(tempId);
    }
  }
}

function handleSeenUpdate(data) {
  const { messageId, userId, userName } = data;
  const { msg } = getByMessageId(messageId);

  if (msg) {
    if (!msg.seen) msg.seen = [];
    if (!msg.seen.some((u) => u.userId === userId)) {
      msg.seen.push({ userId, userName });
    }
  }
}

function handleSeenConfirmed(data) {
  if (typeof data.unreadCount === "number") {
    unreadCount.value = data.unreadCount;
  }
}

function handleReactionUpdate(data) {
  const { messageId, reactions } = data;
  const { msg } = getByMessageId(messageId);

  if (msg && reactions) {
    msg.reactions = reactions;
  }
}

function handleUserStatusUpdate(data) {
  const { userId, userName, status } = data;
  const idx = onlineUsers.value.findIndex((u) => u.userId === userId);

  if (status === "online") {
    if (idx === -1) {
      onlineUsers.value.push({ userId, userName, status });
    } else {
      onlineUsers.value[idx] = { userId, userName, status };
    }
  } else if (status === "offline" && idx !== -1) {
    onlineUsers.value.splice(idx, 1);
  }
}

function handleUserTyping(data) {
  const { userId, userName, isTyping } = data;

  if (isTyping) {
    // Clear any existing timeout for this user
    const existing = typingUsers.value.get(userId);
    if (existing?.timeout) {
      clearTimeout(existing.timeout);
    }

    // Add user to typing list with auto-clear timeout
    const timeout = setTimeout(() => {
      typingUsers.value.delete(userId);
      typingUsers.value = new Map(typingUsers.value); // Trigger reactivity
    }, 5000); // Clear after 5 seconds if no update

    typingUsers.value.set(userId, { userName, timeout });
    typingUsers.value = new Map(typingUsers.value); // Trigger reactivity
  } else {
    // Remove user from typing list
    const existing = typingUsers.value.get(userId);
    if (existing?.timeout) {
      clearTimeout(existing.timeout);
    }
    typingUsers.value.delete(userId);
    typingUsers.value = new Map(typingUsers.value); // Trigger reactivity
  }
}

function handleMessageHistory(data) {
  const el = messagesContainer.value;
  const oldScrollTop = el?.scrollTop ?? 0;
  const oldScrollHeight = el?.scrollHeight ?? 0;

  if (data.messages && Array.isArray(data.messages)) {
    data.messages.forEach((msg) => {
      if (!getByMessageId(msg.id).msg) {
        msg.reactions = msg.reactions || { thumbsUp: [], heart: [] };
        msg.seen = msg.seen || [];
        addMessage(msg, false, true);

        if (
          !oldestMessageId.value ||
          Number(msg.id) < Number(oldestMessageId.value)
        ) {
          oldestMessageId.value = Number(msg.id);
        }
      }
    });

    // Preserve scroll position
    nextTick(() => {
      const newScrollHeight = el?.scrollHeight ?? 0;
      if (el) {
        el.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
      }
    });

    if (data.messages.length === 0 || data.messages.length < 20) {
      hasMoreMessages.value = false;
    }
  }
  loadingOldMessages.value = false;
}

function disconnectWebSocket() {
  if (ws) {
    ws.close();
    ws = null;
  }
  isAuthenticated.value = false;
  clearTimeout(wsReconnectTimeout);
}

/* =========================
   Activity & Background Tasks
   ========================= */
function trackTabActivity() {
  let hidden, visibilityChange;
  if (typeof document.hidden !== "undefined") {
    hidden = "hidden";
    visibilityChange = "visibilitychange";
  } else if (typeof document.msHidden !== "undefined") {
    hidden = "msHidden";
    visibilityChange = "msvisibilitychange";
  } else if (typeof document.webkitHidden !== "undefined") {
    hidden = "webkitHidden";
    visibilityChange = "webkitvisibilitychange";
  }

  function handleVisibilityChange() {
    isTabVisible.value = !document[hidden];
    if (isTabVisible.value && isOpen.value) {
      // Observer will handle seen tracking automatically
      reobserveUnseenMessages();
    }
  }

  if (
    typeof document.addEventListener !== "undefined" &&
    hidden !== undefined
  ) {
    document.addEventListener(visibilityChange, handleVisibilityChange, false);
  }

  window.addEventListener("focus", () => {
    isTabActive.value = true;
    if (isOpen.value) {
      // Observer will handle seen tracking automatically
      reobserveUnseenMessages();
    }
  });

  window.addEventListener("blur", () => {
    isTabActive.value = false;
  });

  function setActive() {
    isTabActive.value = true;
    lastActivity.value = Date.now();
    clearTimeout(activityTimeout);
    activityTimeout = setTimeout(() => {
      isTabActive.value = false;
    }, 120000);
  }

  document.addEventListener("mousemove", setActive);
  document.addEventListener("keypress", setActive);
  document.addEventListener("click", setActive);
}

function startBackgroundTasks() {
  window.addEventListener("online", () => {
    console.log("Browser is back online, attempting to reconnect...");
    showConnectionStatus("online");

    // If WebSocket is disconnected, reconnect immediately
    if (!ws || ws.readyState === WebSocket.CLOSED) {
      clearTimeout(wsReconnectTimeout);
      wsReconnectAttempts = 0; // Reset backoff
      connectWebSocket();
    }
  });

  window.addEventListener("offline", () => {
    console.log("Browser is offline");
    showConnectionStatus("offline");
  });
}

function requestNotificationPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission();
  }
}

/* =========================
   Root Click (close popovers)
   ========================= */
function handleRootClick(e) {
  if (
    !e.target.closest("#tcw-emoji-toggle") &&
    !e.target.closest("#tcw-emoji-picker")
  ) {
    showEmojiPicker.value = false;
  }
  if (
    !e.target.closest("#tcw-message-input") &&
    !e.target.closest("#tcw-mention-dropdown")
  ) {
    hideMentionSuggestions();
  }
}

/* =========================
   Watchers
   ========================= */
// Watch for changes in visibility conditions to reobserve messages
watch([isOpen, isTabActive, isTabVisible], ([open, active, visible]) => {
  if (open && active && visible) {
    reobserveUnseenMessages();
  }
});

watch(isOpen, (open) => {
  if (open) {
    unreadCount.value = 0;
  }
});

watch(
  messages,
  () => {
    if (!isScrolledUp.value) nextTick(() => scrollToBottom(false));
  },
  { deep: true }
);

/* =========================
   Lifecycle
   ========================= */
// Inject emoji CSS styles into Shadow DOM for consistent rendering
function injectEmojiStyles() {
  const el = document.querySelector("team-chat-widget");
  if (el?.shadowRoot) {
    const existingStyle = el.shadowRoot.getElementById("tcw-emoji-styles");
    if (existingStyle) return; // Already injected

    const style = document.createElement("style");
    style.id = "tcw-emoji-styles";
    style.textContent = `
      .tcw-emoji {
        font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Android Emoji', 'EmojiSymbols', sans-serif;
        font-size: 1.2em;
        line-height: 1;
        vertical-align: baseline;
        display: inline-block;
        font-feature-settings: normal;
        font-variant: normal;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
      }
      
      .tcw-emoji-large {
        font-size: 2.8em;
        line-height: 1.1;
      }
      
      /* Emoji picker button styling */
      .tcw-emoji-btn {
        font-size: 1.4em !important;
        padding: 8px;
        margin: 2px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 2px solid transparent;
        background: rgba(255, 255, 255, 0.1);
        user-select: none;
      }
      
      .tcw-emoji-btn:hover {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.3);
        transform: scale(1.1);
      }
      
      .tcw-emoji-btn:active {
        transform: scale(0.95);
        background: rgba(255, 255, 255, 0.3);
      }
      
      /* Make emoji picker grid layout */
      #tcw-emoji-picker {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        max-width: 320px;
        padding: 8px;
      }
      
      /* Emoji-only message styling */
      .tcw-message-content:has(.tcw-emoji-large) {
        text-align: left;
      }
      
      /* Fallback for browsers that don't support :has() */
      .tcw-message-content .tcw-emoji-large {
        display: inline-block;
      }
      
      /* Subtle shake animation for reactions */
      @keyframes tcw-subtle-shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-1px); }
        75% { transform: translateX(1px); }
      }
      
      .tcw-reaction-shake {
        animation: tcw-subtle-shake 0.2s ease-in-out;
      }
      
      /* Ensure reaction buttons have smooth transitions */
      .tcw-reaction-btn {
        transition: all 0.2s ease;
      }
      
      .tcw-reaction-btn:hover {
        transform: scale(1.05);
      }
      
      .tcw-reaction-btn:active {
        transform: scale(0.95);
      }
      
      /* Phone number link styling */
      .tcw-phone-link {
        color: #007bff;
        text-decoration: none;
        font-weight: 500;
        border-radius: 3px;
        padding: 1px 3px;
        transition: all 0.2s ease;
        background: rgba(0, 123, 255, 0.1);
      }
      
      .tcw-phone-link:hover {
        background: rgba(0, 123, 255, 0.2);
        text-decoration: underline;
        transform: translateY(-1px);
      }
      
      .tcw-phone-link:active {
        transform: translateY(0);
        background: rgba(0, 123, 255, 0.3);
      }
    `;

    el.shadowRoot.appendChild(style);
  }
}

onMounted(() => {
  // Inject widget styles into shadow root
  const el = document.querySelector("team-chat-widget");
  if (el?.shadowRoot) {
    // Inject main widget styles
    const styleEl = document.createElement("style");
    styleEl.textContent = widgetStyles;
    el.shadowRoot.appendChild(styleEl);

    // Also inject external CSS if provided (for customization)
    if (config.cssHref) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = config.cssHref;
      el.shadowRoot.appendChild(link);
    }
  }

  // Inject emoji styles after main CSS
  setTimeout(() => injectEmojiStyles(), 100);

  if (!config.userId) {
    config.userId = `${config.userName}_${config.userSurname}_${Date.now()}`;
  }

  if (!config.apiKey || !config.userName || !config.userSurname) {
    console.error("TeamChatWidget: Missing required configuration");
    return;
  }

  initAudio();
  initVisibilityTracking(); // Initialize the observer

  // Connect via WebSocket
  connectWebSocket();

  trackTabActivity();
  startBackgroundTasks();
  requestNotificationPermission();

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (imageViewer.visible) closeImageViewer();
      else if (seenModal.visible) closeSeenModal();
      else if (reactionModal.visible) closeReactionModal();
      else if (showEmojiPicker.value) showEmojiPicker.value = false;
      else if (showMentionDropdown.value) hideMentionSuggestions();
    }
  });
});

onUnmounted(() => {
  if (seenBatchTimer) clearTimeout(seenBatchTimer);
  if (activityTimeout) clearTimeout(activityTimeout);
  if (scrollTimeout) clearTimeout(scrollTimeout);

  // Clean up the observer
  if (visibilityObserver.value) {
    visibilityObserver.value.disconnect();
    visibilityObserver.value = null;
  }

  // Clean up WebSocket
  if (ws) {
    disconnectWebSocket();
  }
  if (wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
  }

  retryTimeouts.forEach((t) => clearTimeout(t));
});

/* =========================
   Public API
   ========================= */
function destroy() {
  if (seenBatchTimer) clearTimeout(seenBatchTimer);
  if (wsReconnectTimeout) clearTimeout(wsReconnectTimeout);

  // Clean up the observer
  if (visibilityObserver.value) {
    visibilityObserver.value.disconnect();
    visibilityObserver.value = null;
  }

  if (ws) {
    disconnectWebSocket();
  }

  retryTimeouts.forEach((t) => clearTimeout(t));
  messages.value.clear();
  onlineUsers.value = [];
  failedMessages.clear();
  retryTimeouts.clear();
  messageIdToKey.value.clear();
  observedMessages.value.clear();
  pendingSeenIds.value.clear();
  wsReconnectAttempts = 0;
}

function getConnectionType() {
  return isAuthenticated.value ? "WebSocket" : "WebSocket_CONNECTING";
}

defineExpose({
  toggle,
  open: () => {
    if (!isOpen.value) toggle();
  },
  close: () => {
    if (isOpen.value) toggle();
  },
  sendMessage: (message, type = "text") => {
    if (!message || typeof message !== "string") {
      console.error(
        "TeamChatWidget.sendMessage: Message must be a non-empty string"
      );
      return false;
    }
    if (message.length > config.maxMessageLength) {
      console.error(
        `TeamChatWidget.sendMessage: Message exceeds maximum length of ${config.maxMessageLength}`
      );
      return false;
    }
    if (type === "image" && !message.startsWith("data:image")) {
      console.error(
        "TeamChatWidget.sendMessage: Image must be base64 data URL"
      );
      return false;
    }
    messageInput.value = "";
    sendMessage(message);
    return true;
  },
  getConnectionType,
  destroy,
  isOpen,
});
</script>
